import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { DollarSign, Pencil, Loader2, Check, X } from "lucide-react";
import { User } from "../types";
import api from "../utils/api";
import { toast } from "sonner";

interface RemunerationTabProps {
  user: User;
  currentUser: User | null;
  onUserUpdate?: (updatedUser: User) => void;
}

const PACKAGE_ITEMS = [
  { key: "phone", label: "Phone" },
  { key: "laptop", label: "Laptop" },
  { key: "companyVehicle", label: "Company Vehicle" },
  { key: "fuelAllowance", label: "Fuel Allowance" },
  { key: "fuelFOB", label: "Fuel FOB" },
  { key: "tollAllowance", label: "Toll Allowance" },
  { key: "eTag", label: "eTag" },
  { key: "commission", label: "Commission" },
];

export function RemunerationTab({ user, currentUser, onUserUpdate }: RemunerationTabProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [baseSalaryRate, setBaseSalaryRate] = useState<string>("");
  const [commissionsBenefits, setCommissionsBenefits] = useState<string>("");
  const [packageInclusions, setPackageInclusions] = useState<Record<string, boolean>>({});
  const [otherPackageInclusion, setOtherPackageInclusion] = useState<string>("");

  const canEdit = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director";

  // Sync form state from user prop when dialog opens
  useEffect(() => {
    if (isEditOpen) {
      setHourlyRate(user.hourlyRate?.toString() ?? "");
      setBaseSalaryRate(user.baseSalaryRate?.toString() ?? "");
      setCommissionsBenefits(user.commissionsBenefits ?? "");
      setPackageInclusions(
        user.packageInclusions && Object.keys(user.packageInclusions).length > 0
          ? user.packageInclusions
          : {}
      );
      setOtherPackageInclusion(user.otherPackageInclusion ?? "");
    }
  }, [isEditOpen, user]);

  const handleSave = async () => {
    if (!user?.id) return;
    setIsSaving(true);

    try {
      const payload: Partial<User> = {
        hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
        baseSalaryRate: baseSalaryRate ? parseFloat(baseSalaryRate) : undefined,
        commissionsBenefits: commissionsBenefits || undefined,
        packageInclusions: { ...packageInclusions },
        otherPackageInclusion: packageInclusions.other ? otherPackageInclusion || undefined : undefined,
      };

      const data = await api.put(`/api/users/${user.id}`, payload);

      if (data?.success) {
        toast.success("Remuneration updated successfully");
        onUserUpdate?.(data.user);
        setIsEditOpen(false);
      } else {
        toast.error(data?.message || "Failed to update remuneration");
      }
    } catch (error: any) {
      console.error("Error updating remuneration:", error);
      toast.error(error?.data?.message || "Error saving remuneration");
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (value?: string | number | null) => {
    if (value === undefined || value === null || value === "") return "—";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "—";
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const activeInclusions = [
    ...PACKAGE_ITEMS.filter((item) => user.packageInclusions?.[item.key]).map((item) => item.label),
    ...(user.packageInclusions?.other && user.otherPackageInclusion
      ? [`Other: ${user.otherPackageInclusion}`]
      : user.packageInclusions?.other
        ? ["Other"]
        : []),
  ];

  return (
    <div className="space-y-6">
      {/* View Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign size={20} className="text-blue-500" />
              Remuneration
            </CardTitle>
            <CardDescription>
              Compensation details and package benefits for this person.
            </CardDescription>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setIsEditOpen(true)}
            >
              <Pencil size={14} />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Rates */}
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border bg-gray-50/50 p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Hourly Rate
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-gray-900">
                {formatCurrency(user.hourlyRate)}
              </p>
            </div>
            <div className="rounded-lg border bg-gray-50/50 p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Base Salary Rate
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-gray-900">
                {formatCurrency(user.baseSalaryRate)}
              </p>
            </div>
          </div>

          <Separator />

          {/* Commissions & Benefits */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Commissions & Benefits
            </h4>
            {user.commissionsBenefits ? (
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {user.commissionsBenefits}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No details provided</p>
            )}
          </div>

          <Separator />

          {/* Package Inclusions */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Package Inclusions
            </h4>
            {activeInclusions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {activeInclusions.map((item) => (
                  <Badge
                    key={item}
                    variant="outline"
                    className="bg-green-50 border-green-200 text-green-800 hover:bg-green-50 gap-1 px-2.5 py-1"
                  >
                    <Check size={12} />
                    {item}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No package inclusions selected</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign size={18} className="text-blue-500" />
              Edit Remuneration
            </DialogTitle>
            <DialogDescription>
              Update compensation details and package benefits.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Rate Inputs */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="hourlyRate">Hourly Rate</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="e.g. 45"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="baseSalaryRate">Base Salary Rate</Label>
                <Input
                  id="baseSalaryRate"
                  type="number"
                  value={baseSalaryRate}
                  onChange={(e) => setBaseSalaryRate(e.target.value)}
                  placeholder="e.g. 75000"
                />
              </div>
            </div>

            <Separator />

            {/* Commissions & Benefits */}
            <div className="space-y-2">
              <Label htmlFor="commissionsBenefits">Commissions & Benefits</Label>
              <Textarea
                id="commissionsBenefits"
                value={commissionsBenefits}
                onChange={(e) => setCommissionsBenefits(e.target.value)}
                rows={4}
                placeholder="Describe any commissions, bonuses, or additional benefits..."
                className="resize-y"
              />
            </div>

            <Separator />

            {/* Package Inclusions */}
            <div className="space-y-3">
              <div>
                <Label className="text-base">Package Inclusions</Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Select the items included in this person&apos;s employment package.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {PACKAGE_ITEMS.map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2.5">
                    <Checkbox
                      id={`edit-pkg-${key}`}
                      checked={packageInclusions[key] || false}
                      onCheckedChange={(checked) =>
                        setPackageInclusions((prev) => ({
                          ...prev,
                          [key]: checked === true,
                        }))
                      }
                    />
                    <Label htmlFor={`edit-pkg-${key}`} className="font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
                <div className="flex items-center space-x-2.5">
                  <Checkbox
                    id="edit-pkg-other"
                    checked={packageInclusions.other || false}
                    onCheckedChange={(checked) =>
                      setPackageInclusions((prev) => ({
                        ...prev,
                        other: checked === true,
                      }))
                    }
                  />
                  <Label htmlFor="edit-pkg-other" className="font-normal cursor-pointer">
                    Other
                  </Label>
                </div>
              </div>
              {packageInclusions.other && (
                <div className="pt-1">
                  <Input
                    value={otherPackageInclusion}
                    onChange={(e) => setOtherPackageInclusion(e.target.value)}
                    placeholder="Please specify..."
                    className="sm:w-2/3"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEditOpen(false)}
              disabled={isSaving}
              className="gap-1.5"
            >
              <X size={14} />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-1.5">
              {isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={14} />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
